import { useSelector } from 'react-redux';
import { useCallback } from 'react';
import {
  selectUserPermissions,
  selectUserRole,
  selectIsInitialized,
  selectIsLoadingPermissions,
} from '../features/rbac/rbacSlice';

/**
 * Custom hook for checking user permissions
 * 
 * NOTE: This hook only READS permissions from Redux state.
 * Permission fetching is handled by PermissionInitializer component at app root.
 * 
 * @returns {Object} Permission utilities and state
 * 
 * @example
 * const { canRead, canWrite, hasPermission, isLoading } = usePermissions();
 * 
 * // Check specific screen access
 * if (canRead('incidents')) { ... }
 * if (canWrite('incidents')) { ... }
 * 
 * // Generic permission check
 * if (hasPermission('dashboard', 'write')) { ... }
 */
export function usePermissions() {
  const permissions = useSelector(selectUserPermissions);
  const { roleId, roleName } = useSelector(selectUserRole);
  const isInitialized = useSelector(selectIsInitialized);
  const isLoading = useSelector(selectIsLoadingPermissions);

  /**
   * Check if user has read permission for a screen
   * @param {string} screenId - The screen identifier
   * @returns {boolean}
   */
  const canRead = useCallback((screenId) => {
    if (!permissions) return false;
    return permissions[screenId]?.read === true;
  }, [permissions]);

  /**
   * Check if user has write permission for a screen
   * @param {string} screenId - The screen identifier
   * @returns {boolean}
   */
  const canWrite = useCallback((screenId) => {
    if (!permissions) return false;
    return permissions[screenId]?.write === true;
  }, [permissions]);

  /**
   * Check if user has specific permission for a screen
   * @param {string} screenId - The screen identifier
   * @param {'read' | 'write'} permissionType - The permission type
   * @returns {boolean}
   */
  const hasPermission = useCallback((screenId, permissionType = 'read') => {
    if (!permissions) return false;
    const screenPermission = permissions[screenId];
    if (!screenPermission) return false;
    return permissionType === 'write' ? screenPermission.write : screenPermission.read;
  }, [permissions]);

  /**
   * Check if user has access to any screens (is authenticated with valid permissions)
   * @returns {boolean}
   */
  const hasAnyPermission = useCallback(() => {
    if (!permissions) return false;
    return Object.values(permissions).some(p => p.read || p.write);
  }, [permissions]);

  /**
   * Get all screen IDs the user can read
   * @returns {string[]}
   */
  const getReadableScreens = useCallback(() => {
    if (!permissions) return [];
    return Object.entries(permissions)
      .filter(([, perm]) => perm.read)
      .map(([screenId]) => screenId);
  }, [permissions]);

  /**
   * Get all screen IDs the user can write
   * @returns {string[]}
   */
  const getWritableScreens = useCallback(() => {
    if (!permissions) return [];
    return Object.entries(permissions)
      .filter(([, perm]) => perm.write)
      .map(([screenId]) => screenId);
  }, [permissions]);

  return {
    // Permission check functions
    canRead,
    canWrite,
    hasPermission,
    hasAnyPermission,
    getReadableScreens,
    getWritableScreens,
    
    // Raw permissions data
    permissions,
    roleId,
    roleName,
    
    // State
    isLoading,
    isInitialized,
  };
}

/**
 * Screen ID constants for type-safe permission checks
 */
export const SCREENS = {
  DASHBOARD: 'dashboard',
  INCIDENTS: 'incidents',
  REPORTS: 'reports',
  CONFIGURATION: 'configuration',
  CUSTOMER_REQUESTS: 'customer-requests',
  SYSTEM_ADMIN: 'system-admin',
  PLAYBOOK_AUTOMATION: 'playbook-automation',
  DETECTION_RULES: 'detection-rules',
};

export default usePermissions;
